import { useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { ArrowDown, ArrowUp, Download, LayoutDashboard, LifeBuoy, Plus, Search } from 'lucide-react'
import { toast } from 'sonner'
import { getErrorMessage } from '@/api/errors'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { EmptyState, ErrorState, LoadingState, PageHeader } from '@/components/composites/states'
import { ToneBadge } from '@/components/composites/status-badge'
import { UserAvatar } from '@/components/composites/user-avatar'
import { usePermission } from '@/features/permissions/usePermission'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { fetchDeskExport, formatDuration, SEVERITY_LABEL, STATE_LABEL, useDeskCases, useDeskMeta, type QueueView, type Severity, type SupportCase } from './api'
import { CaseSheet } from './CaseSheet'
import { LogTicketDialog } from './Dialogs'
import { Countdown, SeverityBadge, useMembersById, useNow } from './parts'

const VIEWS: Array<{ id: QueueView; label: string; hint: string }> = [
  { id: 'mine', label: 'My queue', hint: 'Assigned to you, then your team’s unclaimed tickets' },
  { id: 'triage', label: 'Triage', hint: 'New tickets waiting for a priority and owner' },
  { id: 'all', label: 'All open', hint: 'Every open ticket, least time left first' },
  { id: 'closed', label: 'Closed', hint: 'Closed and dismissed: the last 45 days, or the dates you pick' },
]
const ALL = '__all__'

/**
 * Where an open ticket stands, for the All open view: who has the next move. Every open ticket is in
 * exactly one bucket, so the counts add up to the total. `waiting` is set when helpdesk asked the
 * customer something (or Intercom is waiting on them); a fix awaiting the customer's word is `fixed`.
 */
type Stage = 'triage' | 'helpdesk' | 'engineering' | 'asked' | 'confirming'
const STAGES: Array<{ id: Stage; label: string; hint: string; group: 'us' | 'customer' }> = [
  { id: 'triage', label: 'Needs triage', hint: 'New, with no priority or owner yet', group: 'us' },
  { id: 'helpdesk', label: 'With helpdesk', hint: 'Helpdesk is working on it', group: 'us' },
  { id: 'engineering', label: 'With engineering', hint: 'An engineering team has it, including tickets restored with a workaround while the fix is built', group: 'us' },
  { id: 'asked', label: 'Asked the customer', hint: 'We asked the customer something and are waiting for their answer', group: 'customer' },
  { id: 'confirming', label: 'Fixed · confirming', hint: 'Fixed, waiting for the customer to confirm it works', group: 'customer' },
]
const STAGE_IDS = STAGES.map((s) => s.id)
function stageOf(c: SupportCase): Stage | null {
  if (c.state === 'fixed') return 'confirming'
  if (c.waiting) return 'asked'
  if (c.state === 'triage') return 'triage'
  if (c.state === 'helpdesk') return 'helpdesk'
  if (c.state === 'engineering' || c.state === 'restored') return 'engineering'
  return null
}

/** How the list is ordered. `due` is the server's order: least time left (or, when closed, most recently closed). */
type SortOrder = 'due' | 'oldest' | 'newest'
const SORT_IDS: SortOrder[] = ['due', 'oldest', 'newest']
const sortLabel = (s: SortOrder, closed: boolean) =>
  s === 'oldest' ? 'Oldest first' : s === 'newest' ? 'Newest first' : closed ? 'Recently closed' : 'Least time left'
/** Ordered by when the customer first wrote (the Date created column); ties keep the server's order. */
function sortCases(items: SupportCase[], sort: SortOrder): SupportCase[] {
  if (sort === 'due') return items
  const dir = sort === 'oldest' ? 1 : -1
  return items
    .map((c, i) => ({ c, i, t: Date.parse(c.firstContactAt) }))
    .sort((a, b) => (a.t - b.t) * dir || a.i - b.i)
    .map((x) => x.c)
}

type DateRange = 'any' | 'today' | 'yesterday' | '7d' | '30d' | 'custom'
const RANGES: Array<{ id: DateRange; label: string }> = [
  { id: 'any', label: 'Any date' },
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: 'custom', label: 'Custom range…' },
]
const DAY_MS = 86_400_000
/** Midnight of a yyyy-mm-dd day, in the viewer's own timezone. */
const dayStart = (day: string) => new Date(`${day}T00:00:00`).getTime()
const today = () => {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}
/** The range as ISO instants on the ticket's created date (the customer's first message). */
function rangeBounds(range: DateRange, fromDay: string, toDay: string): { from?: string; to?: string } {
  const iso = (t: number) => new Date(t).toISOString()
  const start = today()
  switch (range) {
    case 'today': return { from: iso(start) }
    case 'yesterday': return { from: iso(start - DAY_MS), to: iso(start - 1) }
    case '7d': return { from: iso(start - 6 * DAY_MS) }
    case '30d': return { from: iso(start - 29 * DAY_MS) }
    case 'custom': return { ...(fromDay ? { from: iso(dayStart(fromDay)) } : {}), ...(toDay ? { to: iso(dayStart(toDay) + DAY_MS - 1) } : {}) }
    default: return {}
  }
}
const createdDay = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: new Date(iso).getFullYear() === new Date().getFullYear() ? undefined : 'numeric' })
const createdTime = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

function Row({ c, now, onOpen }: { c: SupportCase; now: number; onOpen: () => void }) {
  const members = useMembersById()
  const assignee = c.assigneeId ? members.map.get(c.assigneeId) : undefined
  const health = c.clock.health
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          'grid w-full grid-cols-[auto_auto_1fr_auto] items-center gap-x-4 gap-y-1 rounded-lg border bg-card px-4 py-3 text-left shadow-e1 transition-colors hover:border-brand/40 max-sm:grid-cols-1',
          health === 'breached' ? 'border-danger/40' : health === 'at_risk' ? 'border-warning/40' : 'border-border',
        )}
      >
        <div className="flex w-28 flex-col gap-1 max-sm:w-auto max-sm:flex-row">
          <SeverityBadge severity={c.severity} size="xs" />
          <span className="num text-tiny text-text-muted">{c.reference}</span>
        </div>
        <div className="flex w-24 flex-col max-sm:w-auto max-sm:flex-row max-sm:gap-1" title={`Created ${new Date(c.firstContactAt).toLocaleString()}`}>
          <span className="num text-xs font-medium">{createdDay(c.firstContactAt)}</span>
          <span className="num text-tiny text-text-muted">{createdTime(c.firstContactAt)}</span>
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{c.title}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-tiny text-text-muted">
            <span>{c.clientName ?? 'Client not matched'}</span>
            <span>·</span>
            <span>{STATE_LABEL[c.state]}</span>
            <span>·</span>
            <span>{c.ownerTeam ?? 'Helpdesk'}</span>
            {assignee ? (
              <span className="inline-flex items-center gap-1">· <UserAvatar person={assignee} size="xs" />{assignee.displayName}</span>
            ) : c.state === 'engineering' ? (
              <ToneBadge tone="warning" size="xs">Unclaimed</ToneBadge>
            ) : null}
            <span>· waiting {formatDuration(c.ageMs)}</span>
            {c.suggestedSeverity && c.state === 'triage' ? <span>· suggested {SEVERITY_LABEL[c.suggestedSeverity]}{c.suggestedTeam ? ` → ${c.suggestedTeam}` : ''}</span> : null}
            {c.update && Date.parse(c.update.dueAt) < now ? <ToneBadge tone="danger" size="xs">Customer update overdue</ToneBadge> : null}
            {c.waitingFrom && now - Date.parse(c.waitingFrom) >= 72 * 3_600_000 ? <ToneBadge tone="warning" size="xs">No reply 72h · review</ToneBadge> : null}
            {c.eta ? <ToneBadge tone="danger" size="xs">Escalated</ToneBadge> : null}
            {c.outcomeNeeded ? <ToneBadge tone="warning" size="xs">Outcome needed</ToneBadge> : null}
          </p>
        </div>
        <Countdown c={c} now={now} compact />
      </button>
    </li>
  )
}

export function SupportDeskPage() {
  const { workspaceSlug = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const view = (params.get('view') as QueueView) || 'mine'
  const caseId = params.get('case')
  // Kept in the link, so a sorted view survives a refresh and can be shared.
  const sort: SortOrder = SORT_IDS.includes(params.get('sort') as SortOrder) ? (params.get('sort') as SortOrder) : 'due'
  const [team, setTeam] = useState(ALL)
  const [severity, setSeverity] = useState(ALL)
  const [q, setQ] = useState('')
  const [logging, setLogging] = useState(false)
  const meta = useDeskMeta()
  const { can } = usePermission()
  const now = useNow()

  const [range, setRange] = useState<DateRange>('any')
  const [fromDay, setFromDay] = useState('')
  const [toDay, setToDay] = useState('')
  const filters = useMemo(() => {
    const f: Record<string, string> = {}
    if (team !== ALL) f.team = team
    if (severity !== ALL) f.severity = severity
    if (q.trim()) f.q = q.trim()
    Object.assign(f, rangeBounds(range, fromDay, toDay))
    return f
  }, [team, severity, q, range, fromDay, toDay])
  const cases = useDeskCases(view, filters)

  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v)
      else next.delete(k)
    }
    setParams(next, { replace: false })
  }

  // Only on All open; kept in the link like the sort.
  const stage: Stage | null = view === 'all' && STAGE_IDS.includes(params.get('stage') as Stage) ? (params.get('stage') as Stage) : null
  const sorted = useMemo(() => sortCases(cases.data?.items ?? [], sort), [cases.data, sort])
  const stageCounts = useMemo(() => {
    const out: Record<Stage, number> = { triage: 0, helpdesk: 0, engineering: 0, asked: 0, confirming: 0 }
    for (const c of sorted) {
      const st = stageOf(c)
      if (st) out[st] += 1
    }
    return out
  }, [sorted])
  const items = useMemo(() => (stage ? sorted.filter((c) => stageOf(c) === stage) : sorted), [sorted, stage])
  const [exporting, setExporting] = useState(false)
  /** The tickets in this view, as filtered, with every milestone: a CSV that opens in Excel or Sheets. */
  const exportView = () => {
    setExporting(true)
    fetchDeskExport(view === 'mine' ? 'all' : view, { ...(filters.from ? { from: filters.from } : {}), ...(filters.to ? { to: filters.to } : {}) })
      .then((data) => {
        const refs = new Set(items.map((c) => c.reference))
        const narrowed = view === 'mine' || Object.keys(filters).length > 0
        const refCol = data.columns.indexOf('Reference')
        const order = new Map(items.map((c, i) => [c.reference, i]))
        const kept = narrowed ? data.rows.filter((r) => refs.has(r[refCol] ?? '')) : data.rows
        // The file follows the order on screen; anything not on screen keeps the export's order, after it.
        const rows = sort === 'due' ? kept : [...kept].sort((a, b) => (order.get(a[refCol] ?? '') ?? 1e9) - (order.get(b[refCol] ?? '') ?? 1e9))
        const cell = (v: string) => (/[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)
        const csv = '\uFEFF' + [data.columns, ...rows].map((r) => r.map((v) => cell(String(v ?? ''))).join(',')).join('\r\n')
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
        const a = document.createElement('a')
        a.href = url
        a.download = `${data.filename}.csv`
        a.click()
        URL.revokeObjectURL(url)
        toast.success(`Exported ${rows.length} ticket${rows.length === 1 ? '' : 's'}`)
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setExporting(false))
  }
  const counts = {
    breached: items.filter((c) => c.clock.health === 'breached').length,
    atRisk: items.filter((c) => c.clock.health === 'at_risk').length,
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        kicker="Support"
        title="Support queue"
        description={`One clock per issue, from the customer's first message. ${sortLabel(sort, view === 'closed')}.`}
        actions={
          <>
            <Button variant="outline" size="sm" disabled={exporting} onClick={exportView}>
              <Download className="size-4" /> {exporting ? 'Exporting…' : 'Export'}
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to={paths.supportDeskDashboard(workspaceSlug)}><LayoutDashboard className="size-4" /> Dashboard</Link>
            </Button>
            {can('update', 'Card') ? (
              <Button size="sm" onClick={() => setLogging(true)}><Plus className="size-4" /> Log ticket</Button>
            ) : null}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <nav className="flex flex-wrap gap-1 rounded-md bg-muted p-1" aria-label="Queue">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              title={v.hint}
              onClick={() => set({ view: v.id, stage: null })}
              className={cn('rounded px-3 py-1 text-sm', view === v.id ? 'bg-card font-medium shadow-sm' : 'text-text-secondary hover:bg-card/60')}
            >
              {v.label}
            </button>
          ))}
        </nav>
        <Select value={team} onValueChange={setTeam}>
          <SelectTrigger className="h-8 w-40 text-xs" aria-label="Team"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All teams</SelectItem>
            <SelectItem value="Helpdesk">Helpdesk</SelectItem>
            {(meta.data?.teams ?? []).map((t) => <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="h-8 w-36 text-xs" aria-label="Priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All priorities</SelectItem>
            <SelectItem value="untriaged">Untriaged</SelectItem>
            {(['P1', 'P2', 'P3', 'P4', 'REQ'] as Severity[]).map((s) => <SelectItem key={s} value={s}>{SEVERITY_LABEL[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-text-muted" />
          <Input className="h-8 w-56 pl-7 text-xs" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, client, reference" />
        </div>
        <Select value={range} onValueChange={(v) => setRange(v as DateRange)}>
          <SelectTrigger className="h-8 w-36 text-xs" aria-label="Created"><SelectValue /></SelectTrigger>
          <SelectContent>{RANGES.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => set({ sort: v === 'due' ? null : v })}>
          <SelectTrigger className="h-8 w-40 text-xs" aria-label="Sort"><SelectValue /></SelectTrigger>
          <SelectContent>{SORT_IDS.map((s) => <SelectItem key={s} value={s}>{sortLabel(s, view === 'closed')}</SelectItem>)}</SelectContent>
        </Select>
        {range === 'custom' ? (
          <div className="flex items-center gap-1 text-xs text-text-secondary">
            <Input type="date" aria-label="Created from" className="h-8 w-36 text-xs" value={fromDay} max={toDay || undefined} onChange={(e) => setFromDay(e.target.value)} />
            <span>to</span>
            <Input type="date" aria-label="Created to" className="h-8 w-36 text-xs" value={toDay} min={fromDay || undefined} onChange={(e) => setToDay(e.target.value)} />
          </div>
        ) : null}
        {view !== 'closed' ? (
          <span className="ml-auto text-xs text-text-muted">
            {items.length} open · <span className={counts.breached ? 'font-semibold text-danger' : ''}>{counts.breached} breached</span> · <span className={counts.atRisk ? 'font-semibold text-warning' : ''}>{counts.atRisk} at risk</span>
          </span>
        ) : null}
      </div>

      {view === 'all' ? (
        <div className="-mt-2 mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs" role="group" aria-label="Where the ticket stands">
          <button
            type="button"
            onClick={() => set({ stage: null })}
            className={cn('rounded-full border px-3 py-1', !stage ? 'border-brand bg-brand-softer font-medium text-foreground' : 'border-border text-text-secondary hover:bg-muted')}
          >
            All · {sorted.length}
          </button>
          {(['us', 'customer'] as const).map((group) => (
            <div key={group} className="flex flex-wrap items-center gap-1.5">
              <span className="font-medium uppercase tracking-wide text-text-muted">{group === 'us' ? 'In progress' : 'Waiting on customer'}</span>
              {STAGES.filter((st) => st.group === group).map((st) => (
                <button
                  key={st.id}
                  type="button"
                  title={st.hint}
                  aria-pressed={stage === st.id}
                  onClick={() => set({ stage: stage === st.id ? null : st.id })}
                  className={cn('rounded-full border px-3 py-1', stage === st.id ? 'border-brand bg-brand-softer font-medium text-foreground' : 'border-border text-text-secondary hover:bg-muted')}
                >
                  {st.label} · {stageCounts[st.id]}
                </button>
              ))}
            </div>
          ))}
        </div>
      ) : null}

      {cases.isPending ? (
        <LoadingState label="Loading the queue…" />
      ) : cases.error ? (
        <ErrorState error={cases.error} onRetry={cases.refetch} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<LifeBuoy className="size-5" />}
          title={view === 'triage' ? 'Nothing waiting for triage' : view === 'mine' ? 'Nothing on your plate' : 'No tickets here'}
          description={view === 'mine' ? 'Tickets assigned to you, and your team’s unclaimed ones, show up here.' : 'New Intercom conversations and tickets appear within a minute.'}
        />
      ) : (
        <ul className="grid gap-2">
          <li className="grid grid-cols-[auto_auto_1fr_auto] gap-x-4 px-4 text-tiny font-medium uppercase tracking-wide text-text-muted max-sm:hidden">
            <span className="w-28">Priority</span>
            <button
              type="button"
              title={sort === 'oldest' ? 'Showing oldest first. Click for newest first' : 'Click to show oldest first'}
              onClick={() => set({ sort: sort === 'oldest' ? 'newest' : 'oldest' })}
              className={cn('inline-flex w-24 items-center gap-1 text-left uppercase tracking-wide hover:text-foreground', sort !== 'due' && 'text-foreground')}
            >
              Date created
              {sort === 'oldest' ? <ArrowUp className="size-3" /> : sort === 'newest' ? <ArrowDown className="size-3" /> : null}
            </button>
            <span>Ticket</span>
            <span className="text-right">Time left</span>
          </li>
          {items.map((c) => <Row key={c.id} c={c} now={now} onOpen={() => set({ case: c.id })} />)}
        </ul>
      )}

      <CaseSheet id={caseId} onClose={() => set({ case: null })} onOpenCase={(id) => set({ case: id })} />
      {logging ? <LogTicketDialog open onOpenChange={setLogging} onCreated={(c) => set({ case: c.id })} /> : null}
    </div>
  )
}
